import type { ApolloCache } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';
import classNames from 'classnames';
import { AnimatePresence } from 'framer-motion';
import type { Op } from 'quill/core';
import { Delta } from 'quill/core';
import React, { useEffect, useState } from 'react';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import { MenuItem, MenuSeparator } from '../../components/Menu';
import MetaList, { MetaListItem } from '../../components/MetaList';
import OpacityMotionTransition from '../../components/OpacityMotionTransition';
import Tooltip from '../../components/Tooltip';
import PopOver from '../../components/deprecated/PopOver';
import PopOverContainer from '../../components/deprecated/PopOverContainer';
import { EditorActions } from '../../editor/editorSlice';
import type {
  Comment_CommentFragment,
  Comment_PostFragment,
  CreateCommentReactionMutation,
  CreateCommentReactionMutationVariables,
  DeleteCommentMutation,
  DeleteCommentMutationVariables,
  DeleteCommentReactionMutation,
  DeleteCommentReactionMutationVariables,
  DeleteThreadMutation,
  DeleteThreadMutationVariables,
  UpdateCommentMutation,
  UpdateCommentMutationVariables,
  UpdateCommentReactionQuery,
  UpdateCommentReactionQueryVariables,
} from '../../graphql/generated/graphqlTypes';
import checkIcon from '../../icons/check.svg';
import editIcon from '../../icons/edit.svg';
import linkIcon from '../../icons/link.svg';
import reactionIcon from '../../icons/reaction.svg';
import trashIcon from '../../icons/trash.svg';
import UIActions from '../../ui/UIActions';
import updateApolloList from '../../utils/graphql/updateApolloList';
import { Link } from '../../utils/router/v3Bridge';
import commentThreadHash from '../../utils/url/commentThreadHash';
import postPath from '../../utils/url/postPath';
import userPath from '../../utils/url/userPath';
import useIsPublicView from '../../utils/useIsPublicView';
import type { ResizeHandler } from '../MagneticWall';
import MagneticWallItemAvatar from '../MagneticWallItemAvatar';
import styles from './Comment.module.scss';
import CommentAuthor from './CommentAuthor';
import MagneticWallItemOverflowMenu, {
  MagneticWallOverflowButton,
} from './MagneticWallItemOverflowMenu';
import CommentInput from './CommentInput';
import CommentMenu from './CommentMenu';
import CommentPreview, {
  ANIMATED_COMMENT_PREVIEW_DURATION,
} from './CommentPreview';
import CommentReactionList from './CommentReactionList';
import EmojiPicker from './EmojiPicker';
import isCommentContentEmpty from './isCommentContentEmpty';
import showFullPageSheet from './showFullPageSheet';
import fullOrgUrl from '../../utils/url/fullOrgUrl';

const COMMENT_META_MARGIN_BOTTOM = parseInt(styles.commentMetaMarginBottom, 10);

const POST_FRAGMENT = gql`
  fragment Comment_post on RootPostFields {
    id
    ...postPath_post
  }
  ${postPath.fragments.post}
`;

const MAX_LINE_COUNT = 3;
const HOVERED_MAX_LINE_COUNT = 7;
const EXPANDED_MAX_LINE_COUNT = 8;
const LARGE_COMMENT_LENGTH_THRESHOLD = 400;

const DELETE_COMMENT_MUTATION = gql`
  mutation deleteComment($id: ID!) {
    deleteComment(commentId: $id) {
      id
    }
  }
`;

const DELETE_THREAD_MUTATION = gql`
  mutation deleteThread($id: ID!) {
    deleteThread(threadId: $id) {
      id
    }
  }
`;

const UPDATE_COMMENT_MUTATION = gql`
  mutation updateComment($id: ID!, $content: String!) {
    updateComment(commentId: $id, content: $content) {
      id
      content
    }
  }
`;

const COMMENT_FRAGMENT = gql`
  fragment Comment_comment on PublicCommentFields {
    id
    content
    author {
      id
      ...userPath_user
      ...MagneticWallItemAvatar_user
      ...CommentAuthor_author
    }
    reactions {
      ...CommentReactionList_reactions
    }
    insertedAt
  }
  ${userPath.fragments.user}
  ${MagneticWallItemAvatar.fragments.user}
  ${CommentAuthor.fragments.author}
  ${CommentReactionList.fragments.reactions}
`;

const CREATE_COMMENT_REACTION_MUTATION = gql`
  mutation createCommentReaction($commentId: ID!, $emoji: String!) {
    commentReaction: createCommentReaction(
      commentId: $commentId
      emoji: $emoji
    ) {
      ...CommentReactionList_reactions
    }
  }
  ${CommentReactionList.fragments.reactions}
`;

const DELETE_COMMENT_REACTION_MUTATION = gql`
  mutation deleteCommentReaction($id: ID!) {
    commentReaction: deleteCommentReaction(id: $id) {
      ...CommentReactionList_reactions
    }
  }
  ${CommentReactionList.fragments.reactions}
`;

const UPDATE_COMMENT_REACTION_QUERY = gql`
  query updateCommentReaction($postId: ID!) {
    post(id: $postId) {
      id
      threads {
        id
        comments {
          id
          reactions {
            id
          }
        }
      }
    }
  }
`;

const updateCommentReactionList = <
  Data extends { [key in NewItemKey]?: NewItem | null },
  NewItemKey extends string,
  NewItem extends Item,
  Item extends { id?: string | null } | string,
>(
  { threadId, commentId }: { threadId: string; commentId: string },
  prev: UpdateCommentReactionQuery,
  data: Data,
  itemKey: string,
  add = true,
) => ({
  ...prev,
  post: prev.post && {
    ...prev.post,
    threads: prev.post.threads?.map((thread) =>
      thread.id !== threadId
        ? thread
        : {
            ...thread,
            comments: thread.comments?.map((comment) =>
              comment.id !== commentId
                ? comment
                : updateApolloList(comment, data, 'reactions', itemKey, add),
            ),
          },
    ),
  },
});

function updateCommentReactionQueryList<
  Cache,
  Response extends { data?: Data | null },
  Data extends { [key in NewItemKey]?: NewItem | null },
  NewItemKey extends string,
  NewItem extends Item,
  Item extends { id?: string | null } | string,
>(
  {
    postId,
    threadId,
    commentId,
  }: { postId: string; threadId: string; commentId: string },
  dataKey: string,
  add = true,
) {
  return (proxy: ApolloCache<Cache>, { data }: Response) => {
    try {
      const query = UPDATE_COMMENT_REACTION_QUERY;
      const prev = proxy.readQuery<
        UpdateCommentReactionQuery,
        UpdateCommentReactionQueryVariables
      >({ query, variables: { postId } });
      if (prev && data) {
        const updated = updateCommentReactionList(
          { threadId, commentId },
          prev,
          data,
          dataKey,
          add,
        );
        proxy.writeQuery({ query, variables: { postId }, data: updated });
      }
    } catch (e) {
      // readQuery failed
    }
  };
}

interface CommentProps {
  comment: Comment_CommentFragment;
  post: Comment_PostFragment;
  onResize: ResizeHandler;
  onResolve: () => void;
  threadId: string;
  first: boolean;
  viewState: 'collapsed' | 'hovered' | 'expanded';
  initialShowingMore?: boolean;
  editing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
}

const Comment = ({
  comment,
  onResize,
  onResolve,
  threadId,
  post,
  first,
  viewState,
  initialShowingMore = false,
  editing,
  onEdit,
  onCancelEdit,
}: CommentProps) => {
  const dispatch = useAppDispatch();

  const [isSaving, setIsSaving] = useState(false);
  const [isShowingMore, setIsShowingMore] = useState(initialShowingMore);
  const [isShowingButtonGroup, setIsShowingButtonGroup] = useState(false);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [editContent, setEditContent] = useState<Op[] | null>(null);
  const [canBeClamped, setCanBeClamped] = useState<boolean | null>(null);

  const currentUser = useAppSelector((state) => state.session.user);
  const isFullPageSheet = showFullPageSheet();
  const isPublicView = useIsPublicView();

  const [deleteThreadMutation] = useMutation<
    DeleteThreadMutation,
    DeleteThreadMutationVariables
  >(DELETE_THREAD_MUTATION);

  const [deleteCommentMutation] = useMutation<
    DeleteCommentMutation,
    DeleteCommentMutationVariables
  >(DELETE_COMMENT_MUTATION, {
    update: (proxy, { data }) => {
      if (data?.deleteComment) {
        proxy.modify({
          id: proxy.identify({ __typename: 'Thread', id: threadId }),
          fields: {
            comments: (existingComments, { readField }) =>
              existingComments.filter(
                (existingComment: { id: string }) =>
                  readField('id', existingComment) !== data.deleteComment.id,
              ),
          },
        });
      }
    },
  });

  const [updateCommentMutation] = useMutation<
    UpdateCommentMutation,
    UpdateCommentMutationVariables
  >(UPDATE_COMMENT_MUTATION);

  const [createCommentReactionMutation] = useMutation<
    CreateCommentReactionMutation,
    CreateCommentReactionMutationVariables
  >(CREATE_COMMENT_REACTION_MUTATION);

  const [deleteCommentReactionMutation] = useMutation<
    DeleteCommentReactionMutation,
    DeleteCommentReactionMutationVariables
  >(DELETE_COMMENT_REACTION_MUTATION);

  const deleteThread = () => {
    dispatch(
      UIActions.confirmModal({
        title: 'Delete entire thread?',
        message: 'All comments in the thread will be deleted.',
        confirmButtonText: 'Delete',
        onConfirm: () => deleteThreadMutation({ variables: { id: threadId } }),
      }),
    );
  };

  const deleteComment = () =>
    deleteCommentMutation({ variables: { id: comment.id } });

  const updateComment = (content: string) =>
    updateCommentMutation({ variables: { id: comment.id, content } });

  const createCommentReaction = (emoji: string) =>
    createCommentReactionMutation({
      variables: { commentId: comment.id, emoji },
      update: updateCommentReactionQueryList(
        { threadId, postId: post.id, commentId: comment.id },
        'commentReaction',
        true,
      ),
    });

  const deleteCommentReaction = (id: string) =>
    deleteCommentReactionMutation({
      variables: { id },
      update: updateCommentReactionQueryList(
        { threadId, postId: post.id, commentId: comment.id },
        'commentReaction',
        false,
      ),
    });

  const focusCommentThread = () => {
    dispatch(EditorActions.focusComment({ id: threadId, isActive: true }));
  };

  const onEnterEdit = () => {
    focusCommentThread();
    onEdit();
    setEditContent(comment.content);
  };

  const onCopyLink = () => {
    const url = fullOrgUrl(`${postPath(post)}${commentThreadHash(threadId)}`);

    navigator.clipboard.writeText(url).then(() => {
      dispatch(
        UIActions.pushToast({
          level: 'success',
          message: 'Link copied to clipboard',
        }),
      );
    });
  };

  const onEditChange = (newContent: Op[]) => {
    setEditContent(newContent);
    onResize();
  };

  const onEmojiPickerOpenChange = (open: boolean) => {
    setIsEmojiPickerOpen(open);
  };

  const onSave = async () => {
    if (!editContent?.length) {
      return;
    }

    setIsSaving(true);

    try {
      await updateComment(JSON.stringify(editContent));
      onCancelEdit();
    } catch {
      dispatch(
        UIActions.pushToast({
          level: 'error',
          message:
            'Oops, we could not save the comment. Please try again or contact support.',
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const getCommentMenuItems = (includingReactionItem: boolean) => {
    const isAuthor = comment.author.id === currentUser.id;
    const menuItems = [];

    if (first && isFullPageSheet) {
      menuItems.push(
        <MenuItem key="resolve" icon={checkIcon} onClick={onResolve}>
          Resolve
        </MenuItem>,
      );
    }

    if (isAuthor) {
      menuItems.push(
        <MenuItem key="edit" icon={editIcon} onClick={onEnterEdit}>
          Edit comment
        </MenuItem>,
      );
    }

    if (first) {
      menuItems.push(
        <MenuItem key="copy" icon={linkIcon} onClick={onCopyLink}>
          Copy link to thread
        </MenuItem>,
      );
    }

    if (includingReactionItem) {
      menuItems.push(
        <MenuItem
          key="reaction"
          icon={reactionIcon}
          onClick={() => onEmojiPickerOpenChange(true)}
        >
          Add reaction
        </MenuItem>,
      );
    }

    if (isAuthor) {
      if (menuItems.length > 0) {
        menuItems.push(<MenuSeparator key="separator" />);
      }
      if (first) {
        menuItems.push(
          <MenuItem
            icon={trashIcon}
            key="delete_thread"
            danger
            onClick={deleteThread}
          >
            Delete thread
          </MenuItem>,
        );
      } else {
        menuItems.push(
          <MenuItem
            icon={trashIcon}
            key="delete"
            danger
            onClick={deleteComment}
          >
            Delete comment
          </MenuItem>,
        );
      }
    }

    return menuItems;
  };

  useEffect(() => {
    if (viewState === 'collapsed') {
      setIsShowingMore(false);
    }
  }, [viewState]);

  const toggleShowMore = () => {
    setIsShowingMore(!isShowingMore);
    focusCommentThread();
    onResize();
  };

  const toggleCommentReaction = (emoji: string) => {
    // TODO: remove this temporary assignment when we upgrade to TypeScript 5.2.
    const commentReactions: {
      id: string;
      emoji: string;
      author: { id: string };
    }[] = comment.reactions;
    const reaction = commentReactions.find(
      (item) => item.emoji === emoji && item.author.id === currentUser.id,
    );

    focusCommentThread();

    if (reaction) {
      deleteCommentReaction(reaction.id);
    } else {
      dispatch(UIActions.addToRecentlyUsedEmojis(emoji));
      createCommentReaction(emoji);
    }
  };

  const renderCommentActions = () => {
    const actions = [];

    if (canBeClamped && !editing) {
      actions.push(
        <MetaListItem key="show-more">
          <Button variant="link" size="micro" onClick={toggleShowMore}>
            {isShowingMore ? 'Show less' : 'Show more'}
          </Button>
        </MetaListItem>,
      );
    }
    if (editing) {
      actions.push(
        <MetaListItem key="edit">
          <Button
            disabled={isSaving || isCommentContentEmpty(editContent)}
            variant="link"
            size="micro"
            onClick={onSave}
          >
            Save
          </Button>
        </MetaListItem>,
        <MetaListItem key="cancel">
          <Button variant="link" size="micro" onClick={onCancelEdit}>
            Cancel
          </Button>
        </MetaListItem>,
      );
    }

    if (!actions.length) {
      return null;
    }

    return (
      <OpacityMotionTransition
        key="comment-actions"
        className="comment-actions"
        transition={{ duration: ANIMATED_COMMENT_PREVIEW_DURATION }}
        animate={{
          height: 'auto',
          marginTop: COMMENT_META_MARGIN_BOTTOM,
        }}
        initial={{ height: 0, marginTop: 0 }}
        exit={{ height: 0, marginTop: 0 }}
      >
        <MetaList>{actions}</MetaList>
      </OpacityMotionTransition>
    );
  };

  const avatar = <MagneticWallItemAvatar user={comment.author} />;
  const commonButtonProps = {
    emojiPickerOpen: isEmojiPickerOpen,
    onEmojiPickerOpenChange,
    toggleCommentReaction,
  };

  const maxLineCount = (() => {
    switch (viewState) {
      case 'hovered':
        return HOVERED_MAX_LINE_COUNT;
      case 'expanded':
        return EXPANDED_MAX_LINE_COUNT;
      default:
        return MAX_LINE_COUNT;
    }
  })();

  return (
    <div
      className={classNames('comment', {
        'comment-large':
          viewState !== 'collapsed' &&
          isShowingMore &&
          new Delta(comment.content).length() > LARGE_COMMENT_LENGTH_THRESHOLD,
      })}
    >
      <div className="comment-left">
        {viewState !== 'collapsed' && !isPublicView ? (
          <Link to={userPath(comment.author)}>{avatar}</Link>
        ) : (
          avatar
        )}
      </div>
      <div
        className={classNames('comment-right', {
          'comment-right-sheet': isFullPageSheet,
        })}
        onMouseEnter={() => setIsShowingButtonGroup(true)}
        onMouseLeave={() => setIsShowingButtonGroup(false)}
      >
        <AnimatePresence initial={false}>
          {viewState !== 'collapsed' && (
            <OpacityMotionTransition
              key="comment-meta"
              animate={{
                height: 'auto',
                marginBottom: COMMENT_META_MARGIN_BOTTOM,
              }}
              initial={{ height: 0, marginBottom: 0 }}
              exit={{ height: 0, marginBottom: 0 }}
              className="comment-meta"
              transition={{ duration: ANIMATED_COMMENT_PREVIEW_DURATION }}
              onAnimationComplete={() => onResize({ immediate: true })}
            >
              <CommentAuthor
                isFirst={first}
                isExpanded
                author={comment.author}
                insertedAt={comment.insertedAt}
                threadId={threadId}
              />
            </OpacityMotionTransition>
          )}
        </AnimatePresence>
        <div className="comment-content">
          {editing && editContent ? (
            <CommentInput
              autoFocus
              initialContent={editContent}
              onChange={onEditChange}
              onSave={onSave}
            />
          ) : (
            <CommentPreview
              animated
              collapseLines={viewState !== 'expanded'}
              content={comment.content}
              link={viewState === 'expanded'}
              showHoverCard={viewState === 'expanded'}
              maxLines={maxLineCount}
              disableLineClamp={viewState !== 'collapsed' && isShowingMore}
              onCanBeClampedChange={setCanBeClamped}
              onResize={() => onResize({ immediate: true })}
            />
          )}
        </div>
        <AnimatePresence initial={false}>
          {!editing && viewState !== 'collapsed' && (
            <OpacityMotionTransition
              key="comment-button"
              className="comment-button"
              transition={{ duration: ANIMATED_COMMENT_PREVIEW_DURATION }}
            >
              {isFullPageSheet ? (
                <CommentMenu
                  menuItems={getCommentMenuItems(true)}
                  {...commonButtonProps}
                />
              ) : (
                ((viewState === 'expanded' && isShowingButtonGroup) ||
                  (viewState === 'hovered' && first)) && (
                  <MagneticWallItemOverflowMenu
                    menuItems={getCommentMenuItems(false)}
                  >
                    {/* @ts-expect-error */}
                    <PopOver
                      placement="bottom-end"
                      open={isEmojiPickerOpen}
                      onOpenChange={onEmojiPickerOpenChange}
                      disablePersist
                      preventCloseOnContentClick
                      trigger="click"
                      popOver={
                        <PopOverContainer noPadding>
                          <EmojiPicker
                            onPick={(emoji) => {
                              onEmojiPickerOpenChange(false);
                              toggleCommentReaction(emoji);
                            }}
                          />
                        </PopOverContainer>
                      }
                      popOverPortal={document.body}
                    >
                      <MagneticWallOverflowButton
                        icon={reactionIcon}
                        active={isEmojiPickerOpen}
                      />
                    </PopOver>
                    {first && (
                      <Tooltip
                        placement="top"
                        tooltip="Resolve"
                        popOverPortal={document.body}
                        trigger="hover"
                      >
                        <MagneticWallOverflowButton
                          onClick={onResolve}
                          icon={checkIcon}
                        />
                      </Tooltip>
                    )}
                  </MagneticWallItemOverflowMenu>
                )
              )}
            </OpacityMotionTransition>
          )}
          {viewState !== 'collapsed' && <>{renderCommentActions()}</>}
          {viewState === 'expanded' &&
            comment.reactions &&
            comment.reactions.length > 0 && (
              <OpacityMotionTransition
                key="comment-reactions"
                className="comment-reactions"
              >
                <CommentReactionList
                  reactions={comment.reactions}
                  toggleCommentReaction={toggleCommentReaction}
                />
              </OpacityMotionTransition>
            )}
        </AnimatePresence>
      </div>
    </div>
  );
};

Comment.fragments = {
  post: POST_FRAGMENT,
  comment: COMMENT_FRAGMENT,
};

export default Comment;
